import React, { useState } from "react";
import { Button, IconButton, TextField, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import BookmarksIcon from "@material-ui/icons/Bookmarks";
import DeleteIcon from "@material-ui/icons/Delete";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import swal from "sweetalert";

import CustomGreenButton from "../../../../../components/shared/custom-green-button";
import {
  createPlantilla,
  deletePlantilla,
  editPlantilla,
  fetchPlantillas,
} from "../../../../../shared/fetchers/recomendacionPlantillas";

const QUERY_KEY = ["recomendacion-plantillas"];

/**
 * Guarda y recupera configuraciones completas del recomendador.
 *
 * Lo que se guarda es la configuración, no el resultado: al cargar una plantilla se
 * vuelve a generar con los datos del momento.
 */
const PlantillaBarra = ({ config, onCargar }) => {
  const queryClient = useQueryClient();
  const [seleccionada, setSeleccionada] = useState(null);

  const { data: plantillas = [] } = useQuery(QUERY_KEY, () =>
    fetchPlantillas()
  );

  const invalidar = () => queryClient.invalidateQueries(QUERY_KEY);

  const guardar = useMutation(createPlantilla, {
    onSuccess: (data) => {
      if (data?.status !== 1) {
        toast.error(data?.info || "No se pudo guardar la plantilla");
        return;
      }
      toast.success("Plantilla guardada");
      invalidar();
    },
    onError: (error) => toast.error(error?.message || "No se pudo guardar"),
  });

  const sobrescribir = useMutation(editPlantilla, {
    onSuccess: (data) => {
      if (data?.status !== 1) {
        toast.error(data?.info || "No se pudo actualizar la plantilla");
        return;
      }
      toast.success("Plantilla actualizada");
      invalidar();
    },
    onError: (error) => toast.error(error?.message || "No se pudo actualizar"),
  });

  const eliminar = useMutation(deletePlantilla, {
    onSuccess: (data) => {
      if (data?.status !== 1) {
        toast.error(data?.info || "No se pudo eliminar la plantilla");
        return;
      }
      toast.success("Plantilla eliminada");
      setSeleccionada(null);
      invalidar();
    },
    onError: (error) => toast.error(error?.message || "No se pudo eliminar"),
  });

  const handleGuardarNueva = async () => {
    const nombre = (config?.nombre || "").trim();
    if (!nombre) {
      toast.error("Ingresa un nombre en los parámetros antes de guardar");
      return;
    }
    guardar.mutate({ nombre, config });
  };

  const handleSobrescribir = () => {
    if (!seleccionada) return;
    sobrescribir.mutate({
      id: seleccionada.id,
      nombre: (config?.nombre || seleccionada.nombre).trim(),
      config,
    });
  };

  const handleCargar = () => {
    if (!seleccionada?.config) {
      toast.error("La plantilla no tiene una configuración válida");
      return;
    }
    onCargar(seleccionada.config);
    toast.success(`Plantilla "${seleccionada.nombre}" cargada`);
  };

  const handleEliminar = async () => {
    if (!seleccionada) return;
    const confirmado = await swal({
      title: "¿Eliminar plantilla?",
      text: `Se eliminará "${seleccionada.nombre}"`,
      icon: "warning",
      buttons: ["Cancelar", "Eliminar"],
      dangerMode: true,
    });
    if (!confirmado) return;
    eliminar.mutate(seleccionada.id);
  };

  return (
    <div>
      <div className="d-flex align-items-center">
        <BookmarksIcon style={{ fontSize: "17px" }} />
        <div className="admin-main-title-dark-gray ml-2 mt-1">Plantillas</div>
      </div>
      <div className="admin-muted-text-small mt-1 mb-2">
        Guarda esta configuración para reutilizarla en la próxima renovación.
      </div>

      <div className="d-flex flex-wrap align-items-center">
        <Autocomplete
          options={plantillas}
          getOptionLabel={(option) =>
            option?.superInstituto
              ? `${option.nombre} — ${option.superInstituto}`
              : (option?.nombre ?? "")
          }
          getOptionSelected={(option, valor) => option?.id === valor?.id}
          value={seleccionada}
          onChange={(_, valor) => setSeleccionada(valor)}
          noOptionsText="Sin plantillas guardadas"
          className="mr-2 mb-2"
          style={{ width: "340px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder="Selecciona una plantilla"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            />
          )}
        />

        <Button
          size="small"
          className="mr-2 mb-2"
          disabled={!seleccionada}
          onClick={handleCargar}
        >
          Cargar
        </Button>
        <Button
          size="small"
          className="mr-2 mb-2"
          disabled={!seleccionada || sobrescribir.isLoading}
          onClick={handleSobrescribir}
        >
          Sobrescribir
        </Button>
        <CustomGreenButton
          variant="contained"
          className="mr-2 mb-2"
          disabled={guardar.isLoading}
          onClick={handleGuardarNueva}
        >
          Guardar como nueva
        </CustomGreenButton>
        <Tooltip title="Eliminar plantilla">
          <span className="mb-2">
            <IconButton
              size="small"
              disabled={!seleccionada || eliminar.isLoading}
              onClick={handleEliminar}
            >
              <DeleteIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default PlantillaBarra;
